import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Markdown } from 'react-showdown';

import { withAppContext } from '../../contexts/app';

import Button from 'shared/Button';

import ConditionPhotographies from '../../components/ConditionPhotographies';
import ConditionSnomedICD from '../../components/ConditionSnomedICD';
import Expando from '../../components/Expando';

import SegmentAnalytics from '../../../../helpers/SegmentAnalytics.js';

const confidenceLabels = {
  high: {
    color: 'green',
    label: 'High confidence'
  },
  medium: {
    color: 'yellow',
    label: 'Medium confidence'
  },
  low: {
    color: 'red',
    label: 'Low confidence'
  }
};

const malignancyLabels = {
  malignant: {
    color: 'red',
    label: 'Malignant'
  },
  premalignant: {
    color: 'yellow',
    label: 'Premalignant'
  }
};

class ConditionCard extends React.Component {
  constructor(props) {
    super(props);
  }

  render() {
    const { isOfflineMode } = this.props.app;
    const showGalleryLink = (this.props.type === 'macroscopic'); // We don't have galleries for dermoscopic images yet

    return (
      <div id={this.props.id} className={`results-card condition-card condition-card${this.props.condition.highRisk ? ' condition-card--severe' : ''}`}>

        <div className="results-card__head">
          { this.props.clinicianMode ? this.cardHeader() : this.healthPageLink(this.cardHeader()) }
        </div>

        <div className="results-card__content">
          <div className="condition-card__tags">{ this.cardTags() }</div>

          <div className="condition-card__intro">
            <div className="condition-card__intro__description">
              <Markdown markup={ this.props.condition.description } />
            </div>

            { this.props.condition.symptoms &&
              <div className="condition-card__intro__symptoms">
                <h4>Symptoms</h4>
                <ul>
                  { this.props.condition.symptoms.map((symptom, index) => (
                    <li key={index}>{ symptom }</li>
                  )) }
                </ul>
              </div>
            }
          </div>

          { this.props.clinicianMode ?
            <div className="condition-card__expandos">
              { this.props.condition.sampleImages && this.props.condition.sampleImages.length > 0 &&
                <Expando
                  title='Photos'
                  className="condition-card____photos"
                  defaultOpen={true}
                  header={isOfflineMode ? null : // Linking to the gallery won't work in offline mode, so just use the title
                    <div className="expando__title">
                      <div className="expando__title__left">
                        <h4>Photos</h4>
                      </div>

                      <div className="expando__title__right">
                        { showGalleryLink &&
                          <Link
                            className="condition-card__photos-link__clinician"
                            to={{
                              pathname: `/health/conditions/${this.props.condition.id}/photos`,
                              state: { backButtonLabel: 'Back to results' }}
                            }>
                            <Button className="button button--small">
                              See all {this.props.condition.sampleImages.length} photos
                            </Button>
                          </Link>
                        }
                      </div>
                    </div>
                  }
                  content={
                    <ConditionPhotographies
                      className="condition-card__expando-photos"
                      images={this.props.condition.sampleImages}
                      linkToGallery={showGalleryLink}
                      lightboxEnabled={true}
                      galleryBackButtonText='Back to results'
                    />
                  }
                />
              }

              <Expando
                title='SNOMED & ICD codes'
                content={
                  <ConditionSnomedICD codes={this.props.condition.codes} />
                }
              />
            </div>
            :
            <div className="condition-card__photos">
              { this.props.condition.sampleImages && this.props.condition.sampleImages.length > 0 &&
                <ConditionPhotographies
                  title={'Photos'}
                  titleHeaderTag={'h4'}
                  images={this.props.condition.sampleImages}
                  linkToGallery={showGalleryLink}
                  galleryLink={`/health/conditions/${this.props.condition.id}/photos`}
                  galleryBackButtonLabel='Back to results'
                  hoverToReveal={true}
                />
              }
            </div>
          }
        </div>

        { !this.props.clinicianMode &&
          <div className="condition-card__footer">
            { this.healthPageLink() }
          </div>
        }
      </div>
    );
  }

  cardTags() {
    return (
      <React.Fragment>
        { this.props.type !== 'dermoscopic' &&
          this.riskTag()
        }

        { this.props.clinicianMode &&
          <React.Fragment>
            { this.malignancyTag() }
            { this.confidenceTag() }
          </React.Fragment>
        }
      </React.Fragment>
    );
  }

  cardHeader() {
    return (
      <React.Fragment>
        <span className="condition-card__index">{this.props.index}</span>
        <h3>{ this.props.condition.commonName }</h3>

        { this.cardTags() }
      </React.Fragment>
    );
  }

  riskTag() {
    return (this.props.condition.highRisk) ? <span className="condition-card__tag condition-card__tag--red">High risk condition</span> : '';
  }

  malignancyTag() {
    const item = malignancyLabels[this.props.condition.malignancy];

    if (!item) { return <React.Fragment />; }

    return <span className={`condition-card__tag condition-card__tag--${item.color}`}>{ item.label }</span>;
  }

  confidenceTag() {
    const { isOfflineMode } = this.props.app;
    const probability = this.props.probability;
    const probabilityId = ((probability >= 0.9) ? 'high' : (probability <= 0.1) ? 'low' : 'medium');

    return (
      <span className={`condition-card__tag condition-card__tag--${confidenceLabels[probabilityId].color}`}>
        { (isOfflineMode) ? `${(probability * 100).toFixed(1)}% confidence` : confidenceLabels[probabilityId].label }
      </span>
    );
  }

  healthPageLink(linkContent) {
    const className = !linkContent ? 'condition-card__button' : 'condition-card__head-link';
    const riskText = this.props.condition.highRisk ? ' high risk' : '';
    const buttonTextDesktop = `Learn more about this${riskText} condition`;
    const buttonTextMobile = 'Learn more';

    const healthPagePathFromUrl = (url) => {
      const regExp = /^https:\/\/www.triage.com\/health\/conditions\/(.+)$/g;
      const match = regExp.exec(url);

      return (match ? match[1] : false);
    };

    const conditionButton = (type) => {
      const text = (type === 'mobile') ? buttonTextMobile : buttonTextDesktop;
      const className = `button-text__${(type === 'mobile') ? 'mobile' : 'desktop'}`;
      const iconClassName = 'icon icon--right icon--forward-arrow--white';

      return (
        <span className={className}>{ text }<i className={iconClassName} /></span>
      );
    };

    const internalLinkForHealthPage = (healthPagePath, linkContent) => {
      const linkPath = `${this.props.app.healthPages.basePath}${this.props.app.healthPages.conditionPagesPath}/${healthPagePath}`;
      const onClick = () => {
        SegmentAnalytics.track('Clicked results learn more', { condition: healthPagePath });
      };

      return (
        <Link
          to={{
            pathname: linkPath,
            state: { fromConditionResults: true }
          }}
          className={className}
          onClick={onClick}
        >
          { linkContent ?
            <React.Fragment>{ linkContent }</React.Fragment>
            :
            <React.Fragment>
              { conditionButton('mobile') }
              { conditionButton('desktop') }
            </React.Fragment>
          }
        </Link>
      );
    };

    const externalLink = (url, linkContent) => {
      return (
        <a className={className} target="_blank" rel="noopener noreferrer" href={url}>
          { linkContent ?
            <React.Fragment>{ linkContent }</React.Fragment>
            :
            <React.Fragment>
              { conditionButton('mobile') }
              { conditionButton('desktop') }
            </React.Fragment>
          }
        </a>
      );
    };

    if (this.props.condition.healthPage) {
      return internalLinkForHealthPage(this.props.condition.healthPage, linkContent);
    } else if (healthPagePathFromUrl(this.props.condition.url)) {
      return internalLinkForHealthPage(healthPagePathFromUrl(this.props.condition.url), linkContent);
    } else {
      return externalLink(this.props.condition.url, linkContent);
    }
  }
}

ConditionCard.defaultProps = {
  clinicianMode: false
};

ConditionCard.propTypes = {
  id: PropTypes.string.isRequired,
  app: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  condition: PropTypes.object.isRequired,
  probability: PropTypes.number,
  type: PropTypes.string,
  clinicianMode: PropTypes.bool
};

export { ConditionCard };
export default withAppContext(ConditionCard);
